import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  ArrowRight,
  Dumbbell,
  Flame,
  Activity,
  StretchHorizontal,
  HeartPulse,
  Plus,
  Target,
  X,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type CanonicalCategory =
  | "hypertrophy"
  | "fat_loss"
  | "cardio_fitness"
  | "mobility"
  | "general_health";

export interface ObjectiveCard {
  title: string;
  category: CanonicalCategory;
  reason?: string;
}

interface ObjectivesStepProps {
  suggestions: ObjectiveCard[];
  selected: string[];
  onChange: (next: string[]) => void;
  onBack: () => void;
  onContinue: () => void;
}

const CATEGORY_META: Record<
  CanonicalCategory,
  { icon: typeof Dumbbell; label: string; defaultReason: string }
> = {
  hypertrophy: {
    icon: Dumbbell,
    label: "Reforço muscular",
    defaultReason: "Ganhar força e massa muscular para sustentar a saúde óssea e metabólica.",
  },
  fat_loss: {
    icon: Flame,
    label: "Perda de peso",
    defaultReason: "Reduzir gordura corporal de forma sustentável e melhorar composição.",
  },
  cardio_fitness: {
    icon: Activity,
    label: "Performance aeróbica",
    defaultReason: "Aumentar resistência cardiovascular e capacidade de recuperação.",
  },
  mobility: {
    icon: StretchHorizontal,
    label: "Mobilidade e flexibilidade",
    defaultReason: "Liberar amplitude de movimento e prevenir dores.",
  },
  general_health: {
    icon: HeartPulse,
    label: "Saúde geral / Reabilitação",
    defaultReason: "Trabalho funcional para bem-estar e qualidade de vida.",
  },
};

const CANONICAL_ORDER: CanonicalCategory[] = [
  "hypertrophy",
  "fat_loss",
  "cardio_fitness",
  "mobility",
  "general_health",
];

const buildFinalCards = (suggestions: ObjectiveCard[]): ObjectiveCard[] => {
  const seen = new Set<CanonicalCategory>();
  const out: ObjectiveCard[] = [];
  for (const s of suggestions) {
    if (seen.has(s.category)) continue;
    seen.add(s.category);
    out.push(s);
    if (out.length >= 5) return out;
  }
  for (const cat of CANONICAL_ORDER) {
    if (out.length >= 5) break;
    if (seen.has(cat)) continue;
    out.push({
      title: CATEGORY_META[cat].label,
      category: cat,
      reason: CATEGORY_META[cat].defaultReason,
    });
  }
  return out.slice(0, 5);
};

export function ObjectivesStep({
  suggestions,
  selected,
  onChange,
  onBack,
  onContinue,
}: ObjectivesStepProps) {
  const [customInput, setCustomInput] = useState("");
  const cards = buildFinalCards(suggestions);

  // Custom objectives = selected items not in cards
  const cardTitles = new Set(cards.map((c) => c.title));
  const customs = selected.filter((s) => !cardTitles.has(s));

  const toggle = (title: string) => {
    if (selected.includes(title)) {
      onChange(selected.filter((s) => s !== title));
    } else {
      onChange([...selected, title]);
    }
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    if (selected.includes(v)) {
      setCustomInput("");
      return;
    }
    onChange([...selected, v]);
    setCustomInput("");
  };

  const removeCustom = (title: string) => {
    onChange(selected.filter((s) => s !== title));
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Target className="size-5" />
          </div>
          <div className="flex flex-col gap-1">
            <CardTitle className="text-xl">Quais seus objetivos?</CardTitle>
            <p className="text-sm text-muted-foreground">
              Selecione 1 ou mais. Sugerimos com base no seu perfil de saúde.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cards.map((card) => {
            const meta = CATEGORY_META[card.category];
            const Icon = meta.icon;
            const isSelected = selected.includes(card.title);
            return (
              <button
                key={card.title}
                type="button"
                onClick={() => toggle(card.title)}
                className={cn(
                  "relative text-left rounded-xl border p-4 transition-all flex gap-3 items-start",
                  "hover:border-primary/40 hover:bg-accent/30",
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                    : "border-border bg-card",
                )}
              >
                <div
                  className={cn(
                    "p-2 rounded-lg shrink-0",
                    isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
                  )}
                >
                  <Icon className="size-4" />
                </div>
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{card.title}</span>
                    {isSelected && (
                      <span className="flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground shrink-0">
                        <Check className="size-3" />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {card.reason || meta.defaultReason}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Objetivo personalizado (opcional)</label>
          <div className="flex gap-2">
            <Input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Ex: Preparação para corrida de 10km"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
            />
            <Button type="button" variant="outline" onClick={addCustom} disabled={!customInput.trim()}>
              <Plus data-icon="inline-start" />
              Adicionar
            </Button>
          </div>
          {customs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {customs.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2.5 py-1"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => removeCustom(c)}
                    className="hover:text-primary/70"
                    aria-label={`Remover ${c}`}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 gap-2">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft data-icon="inline-start" />
            Voltar
          </Button>
          <Button onClick={onContinue} disabled={selected.length === 0} size="lg">
            Continuar
            <ArrowRight data-icon="inline-end" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
